import { createContext, useCallback, useContext, useState } from 'react';
import { useDispatch } from 'react-redux';

import { getApiByPortalType } from 'Lib/components/common/table/filesTable/utils/api';
import PortalTypes from 'Lib/constants/app/portalTypes';
import Components from 'Lib/constants/security/components';
import FilePermissions from 'Lib/constants/security/components/files';
import useApi from 'Lib/hooks/useApi';
import useFormApi from 'Lib/hooks/useFormApi';
import useNotify from 'Lib/hooks/useNotify';
import downloadBlob from 'Lib/utilities/downloadBlob';
import { downloadFile, isPath } from 'Lib/utilities/files';

const FilesContext = createContext();

export const useFiles = () => useContext(FilesContext);

/**
 * Provides a context for managing and interacting with files and folders.
 * This context includes state management and functions for various file operations like
 * fetching, searching, updating, sharing, and deleting files and folders.
 *
 * @param {ReactNode} children - The child components that will consume the context.
 * @param {string} objectID - The ID of the object associated with the files and folders.
 * @param {ObjectTypes} objectTypeID - The type ID of the object associated with the files and folders.
 * @param {ContactTypes} contactTypeID - Optional. The contact type ID, used for determining sharing permissions.
 * @param {PortalTypes} portalTypeID -  The portal type ID, used for determining file permissions.
 * @returns {JSX.Element} A `FilesContext.Provider` wrapping the children.
 */
export const FilesProvider = ({
	children,
	objectID,
	objectTypeID,
	contactTypeID,
	portalTypeID = PortalTypes.Manager,
	disableUpload = false
}) => {
	const [files, setFiles] = useState([]);
	const [folders, setFolders] = useState([]);
	const [selectedFiles, setSelectedFiles] = useState([]);
	const [clearSelected, setClearSelected] = useState(() => () => {});
	const [folderContents, setFolderContents] = useState([]);
	const [dropFolder, setDropFolder] = useState(false);
	const [parentIDs, setParentIDs] = useState([]);
	const [shareContacts, setShareContacts] = useState({
		status: false,
		data: []
	});
	const [breadcrumbs, setBreadcrumbs] = useState([]);

	const [isLoading, setIsLoading] = useState(true);
	const [isDownloading, setIsDownloading] = useState(false);
	const [emptyMessage, setEmptyMessage] = useState('No files found.');
	const [isMoving, setIsMoving] = useState(false);
	const [isUploading, setIsUploading] = useState(false);
	const [isDragging, setIsDragging] = useState(false);
	const [addingNewPath, setAddingNewPath] = useState({
		status: false,
		path: '',
		isAdding: false
	});
	const [sharedWithMenu, setSharedWithMenu] = useState({
		status: false,
		anchorEl: null
	});
	const [currentParentPath, setCurrentParentPath] = useState(1);
	const [editModal, setEditModal] = useState({ status: false, data: null });
	const [confirmShareWithModal, setConfirmShareWithModal] = useState({
		status: false,
		data: null,
		form: null
	});
	const [shareFileModal, setShareFileModal] = useState({
		status: false,
		data: null,
		sendEmail: false,
		multi: false
	});
	const [moveFile, setMoveFile] = useState({
		status: false,
		data: null,
		multi: false,
		newDirectory: null,
		newDirectoryName: ''
	});
	const [deleteModal, setDeleteModal] = useState({
		status: false,
		multi: false,
		data: null,
		deleting: false
	});

	const { toastError, toastSuccess } = useNotify();
	const dispatch = useDispatch();

	const api = getApiByPortalType(portalTypeID);

	const getPathApi = useApi(api.getPath);
	const getPathsApi = useApi(api.getPaths);
	const searchFilesApi = useApi(api.searchFiles);
	const createNewPath = useApi(api.createPath);
	const createPathApi = useFormApi(api.createPath);
	const updatePathApi = useFormApi(api.updatePath);
	const handlePathUpdate = useApi(api.updatePath);
	const deletePathApi = useApi(api.deletePath);
	const deleteFileApi = useApi(api.deleteFileAttachment);
	const uploadFileApi = useApi(api.uploadFile);
	const updateFileApi = useFormApi(api.updateFile);
	const handleFileUpdate = useApi(api.updateFileAttachment);
	const handleDeleteMultiple = useApi(api.deleteMultipleFiles);
	const shareMultipleFiles = useApi(api.fileAttachmentShareMulti);
	const moveMultipleFiles = useFormApi(api.fileAttachmentMoveMulti);
	const shareFileContacts = useApi(api.fileAttachmentShareContacts);
	const downloadFileApi = api.downloadFile;
	const downloadMultipleFiles = api.downloadMultipleFiles;

	const fetchFiles = (
		showLoading = false,
		path = 1,
		parentPathID = null,
		sort = null,
		resetMove = true
	) => {
		if (!objectID || !objectTypeID) return;
		if (showLoading) setIsLoading(true);

		const config = {
			objectID,
			objectTypeID,
			sort: sort ? sort : 'fileName:asc'
		};

		return getPathApi(path, config)
			.then(response => {
				if (parentPathID) setParentIDs([...parentIDs, parentPathID]);

				setFiles(response.data);
				setCurrentParentPath(path);
				setBreadcrumbs(
					response.data.parents.map((item, index) => ({
						path: index === 0 ? 'Files' : item.name,
						pathID: item.pathID,
						onClick: () => fetchFiles(true, item.pathID)
					}))
				);
				setFolderContents([
					...response.data.paths,
					...response.data.files
				]);
				setIsLoading(false);

				if (resetMove) {
					clearSelected();
					resetMoveFile();
				}
			})
			.catch(toastError);
	};

	const handleSearch = filter => {
		if (!filter.search)
			return fetchFiles(
				true,
				currentParentPath,
				currentParentPath,
				filter.orderBy
			);
		setIsLoading(true);

		return searchFilesApi(objectID, objectTypeID, { search: filter.search })
			.then(response => {
				if (response.data.length > 0) {
					setBreadcrumbs([]);
					setFiles({ files: response.data });
				} else setEmptyMessage(`No files found for "${filter.search}"`);

				setFolderContents(
					response.data.length > 0 ? [...response.data] : []
				);
			})
			.catch(toastError)
			.finally(() => {
				setIsLoading(false);
			});
	};

	function findFoldersByPath(data) {
		if (currentParentPath == 1) return data.children;

		const findPath = children => {
			for (const child of children) {
				if (child.id === currentParentPath) {
					return [
						{
							name: 'Files',
							id: data.id,
							parentPathID: data.parentPathID
						},
						...child.children
					];
				}

				const found = findPath(child.children);
				if (found) return found;
			}
		};

		return findPath(data.children) || [];
	}

	const fetchPaths = () => {
		getPathsApi({ objectID, objectTypeID })
			.then(response => {
				setFolders(findFoldersByPath(response.data));
			})
			.catch(toastError);
	};

	const fetchShareFileContacts = portalTypeID => {
		setShareContacts({ ...shareContacts, status: true });

		shareFileContacts({ objectID, objectTypeID, portalTypeID })
			.then(response => {
				setShareContacts({
					status: false,
					data: response.data.map(contact => ({
						name: contact.name,
						email: contact.email
					}))
				});
			})
			.catch(error => {
				setShareContacts({ ...shareContacts, status: false });
				toastError(error);
			});
	};

	const onRowClick = data => {
		if (isPath(data)) fetchFiles(true, data.pathID, data);
	};

	const onRowSelected = selected => setSelectedFiles(selected);

	const handleUpdate = (values, form) => {
		const api = isPath(editModal.data) ? updatePathApi : updateFileApi;
		const id = isPath(editModal.data)
			? editModal.data.pathID
			: editModal.data.file.fileID;

		return api(form, id, values)
			.then(() => {
				fetchFiles(false, currentParentPath);
				resetEditModal();
			})
			.catch(toastError);
	};

	/* Share File(s) */
	const sharedWith = file => {
		const sharedWith = [];

		if (file.isSharedWithOwner == '1') sharedWith.push('Owner');
		if (file.isSharedWithVendor == '1') sharedWith.push('Vendor');
		if (file.isSharedWithTenant == '1') sharedWith.push('Tenant');
		if (
			[
				'isSharedWithOwner',
				'isSharedWithTenant',
				'isSharedWithVendor'
			].every(key => file[key] == '0')
		)
			sharedWith.push('--');

		return sharedWith.join(', ');
	};

	const toggleSharedWithMultiMenu = event => {
		setSharedWithMenu(prevState => ({
			status: !prevState.status,
			anchorEl: event?.currentTarget ?? null
		}));
	};

	const toggleShareWithMultiModal = (event, type, isSharing = true) => {
		if (!shareFileModal.status) {
			toggleSharedWithMultiMenu(event);
			fetchShareFileContacts(type);
		} else setShareContacts({ status: false, data: [] });

		setShareFileModal({
			...shareFileModal,
			multi: true,
			status: !shareFileModal.status,
			data: { type: type, isSharing }
		});
	};

	const sharedMultiMenuActions = () => {
		const actions = [];

		const handleShareOptions = (
			portalType,
			permission,
			shareCheck,
			shareLabel
		) => {
			const allShared = selectedFiles.every(file =>
				isPath(file)
					? file[shareCheck] == '1'
					: file.fileAttachment[shareCheck] == '1'
			);

			if (
				api.hasPermission(Components.Files, permission) &&
				api[`allow${shareLabel}Share`](objectTypeID, contactTypeID)
			) {
				if (!allShared) {
					actions.push({
						label: `Share with ${shareLabel}`,
						onClick: e =>
							toggleShareWithMultiModal(
								e,
								PortalTypes[shareLabel]
							)
					});
				} else {
					actions.push({
						label: `Stop sharing with ${shareLabel}`,
						onClick: e =>
							toggleShareWithMultiModal(
								e,
								PortalTypes[shareLabel],
								false
							)
					});
				}
			}
		};

		handleShareOptions(
			'Owner',
			FilePermissions.ShareFilesWithOwner,
			'isSharedWithOwner',
			'Owner'
		);
		handleShareOptions(
			'Vendor',
			FilePermissions.ShareFilesWithVendor,
			'isSharedWithVendor',
			'Vendor'
		);
		handleShareOptions(
			'Tenant',
			FilePermissions.ShareFilesWithTenant,
			'isSharedWithTenant',
			'Tenant'
		);

		return actions;
	};

	const handleShareMultiSubmit = () => {
		const payload = {
			pathIDs: [],
			fileAttachmentIDs: [],
			[`isSharedWith${PortalTypes.getName(shareFileModal.data.type)}`]: `${shareFileModal.data.isSharing ? '1' : '0'}`,
			sendNotification: shareFileModal.sendEmail
		};

		selectedFiles.forEach(data => {
			if (isPath(data)) payload.pathIDs.push(data.pathID);
			else
				payload.fileAttachmentIDs.push(
					data.fileAttachment.fileAttachmentID
				);
		});

		return shareMultipleFiles(payload)
			.then(() => {
				fetchFiles(false, currentParentPath);
				clearSelected();
				resetShareModal();
			})
			.catch(toastError);
	};

	const onShareFile = helperObject => {
		fetchShareFileContacts(PortalTypes[helperObject.type]);

		const updatedState = {
			sendEmail: shareFileModal.sendEmail,
			status: helperObject.sharedWith
				? shareFileModal.status
				: !shareFileModal.status,
			data: helperObject,
			multi: false
		};
		setShareFileModal(updatedState);

		if (helperObject.sharedWith) toggleShareWith(updatedState);
	};

	const resetShareModal = () =>
		setShareFileModal({
			status: false,
			data: null,
			sendEmail: false,
			multi: false
		});

	const toggleShareWith = shareFileHelperObject => {
		const { fileObject, sharedWith, type, api } =
			shareFileHelperObject.data;
		const payload = {
			[`isSharedWith${type}`]: !sharedWith,
			sendNotification: shareFileHelperObject.sendEmail === true
		};

		api(
			isPath(fileObject)
				? fileObject.pathID
				: fileObject.fileAttachmentID,
			payload
		)
			.then(() => {
				fetchFiles(
					false,
					isPath(fileObject)
						? fileObject.parentPathID
						: fileObject.pathID
				);
				resetShareModal();
			})
			.catch(toastError);
	};

	/* Download File(s) */
	const handleDownload = data => {
		setIsDownloading(true);
		const processDownload = isPath(data)
			? downloadMultipleFiles({
					pathIDs: [data.pathID],
					objectID,
					objectTypeID
				}).then(response => {
					downloadBlob(
						new Blob([response.data]),
						response.headers['rentvine-filename']
					);
				})
			: downloadFile(
					downloadFileApi(data.file.fileID, {
						objectID,
						objectTypeID
					})
				);

		processDownload
			.then(clearSelected)
			.catch(toastError)
			.finally(() => setIsDownloading(false));
	};

	const handleBatchDownload = () => {
		if (selectedFiles.length === 1) return handleDownload(selectedFiles[0]);

		setIsDownloading(true);
		const fileIDs = [];
		const pathIDs = [];
		selectedFiles.forEach(item =>
			isPath(item)
				? pathIDs.push(item.pathID)
				: fileIDs.push(item.file.fileID)
		);

		downloadMultipleFiles({ fileIDs, pathIDs, objectID, objectTypeID })
			.then(response => {
				downloadBlob(
					new Blob([response.data]),
					response.headers['rentvine-filename']
				);
				clearSelected();
			})
			.catch(toastError)
			.finally(() => setIsDownloading(false));
	};

	/* Edit File(s) */
	const toggleEditModal = (data = null) =>
		setEditModal(prevState => ({ status: !prevState.status, data }));
	const resetEditModal = () => setEditModal({ status: false, data: null });

	/* Move File(s) */
	const toggleMoveFileModal = (data = null, multi = false) =>
		setMoveFile({
			...moveFile,
			status: !moveFile.status,
			data,
			multi
		});

	const resetMoveFile = () => {
		setMoveFile({
			status: false,
			data: null,
			multi: false,
			newDirectory: null,
			newDirectoryName: ''
		});
		setConfirmShareWithModal({ status: false, data: null, form: null });
	};

	const toggleConfirmShareWithModal = (data = null, form = null) => {
		// Reset drag and drop if that's the way files are being moved
		if (confirmShareWithModal.status && isDragging) {
			resetFilesDrag();
			resetMoveFile();

			return;
		}

		setConfirmShareWithModal(prevState => ({
			data,
			form,
			status: !prevState.status
		}));
	};

	const shouldOpenConfirmMoveModal = () => {
		const newDirectoryPath = files.paths?.find(
			path => path.pathID === moveFile.newDirectory
		);
		if (!newDirectoryPath) return false;

		const newDirectorySharedWith = [
			newDirectoryPath.isSharedWithApplicant,
			newDirectoryPath.isSharedWithOwner,
			newDirectoryPath.isSharedWithTenant,
			newDirectoryPath.isSharedWithVendor
		];
		const isNewDirectoryShared =
			newDirectorySharedWith.includes('1') ||
			newDirectorySharedWith.includes(1);

		if (!isNewDirectoryShared) return false;

		let moveItemsArray = moveFile.data;
		if (moveFile.data && !Array.isArray(moveFile.data))
			moveItemsArray = [moveFile.data];

		const allItemsMatchSharedWith = moveItemsArray?.every(item => {
			const itemSharedWith = item.fileAttachment
				? [
						item.fileAttachment.isSharedWithApplicant,
						item.fileAttachment.isSharedWithOwner,
						item.fileAttachment.isSharedWithTenant,
						item.fileAttachment.isSharedWithVendor
					]
				: [
						item.isSharedWithApplicant,
						item.isSharedWithOwner,
						item.isSharedWithTenant,
						item.isSharedWithVendor
					];

			return newDirectorySharedWith.every(
				(value, index) => value == itemSharedWith[index]
			);
		});

		return !allItemsMatchSharedWith;
	};

	const confirmMove = (values, form) => {
		if (shouldOpenConfirmMoveModal()) {
			form.setSubmitting(false);
			toggleConfirmShareWithModal(values, form);
		} else handleMove(values, form);
	};

	const handleConfirmMove = () => {
		setConfirmShareWithModal(prevState => ({
			...prevState,
			status: false
		}));
		if (isDragging) onDrop(confirmShareWithModal.data);
		else handleMove(confirmShareWithModal.data, confirmShareWithModal.form);
	};

	const handleMove = (values, form) => {
		setIsMoving(true);

		const payload = {
			destinationPathID: values.newDirectory,
			pathIDs: [],
			fileAttachmentIDs: []
		};

		if (moveFile.multi) {
			moveFile.data.forEach(item => {
				if (isPath(item)) payload.pathIDs.push(item.pathID);
				else
					payload.fileAttachmentIDs.push(
						item.fileAttachment.fileAttachmentID
					);
			});
		} else {
			if (isPath(moveFile.data))
				payload.pathIDs.push(moveFile.data.pathID);
			else
				payload.fileAttachmentIDs.push(
					moveFile.data.fileAttachment.fileAttachmentID
				);
		}

		moveMultipleFiles(form, payload)
			.then(() => {
				fetchFiles(false, currentParentPath);
				resetMoveFile();
				toastSuccess('Items moved successfully');
			})
			.catch(toastError)
			.finally(() => setIsMoving(false));
	};

	/* Delete File(s) */
	const toggleDeleteModal = (type, data) => {
		setDeleteModal(prevState => ({
			...prevState,
			status: !prevState.status,
			multi: type === 'multi',
			data
		}));
	};
	const resetDeleteModal = () =>
		setDeleteModal({
			status: false,
			multi: false,
			data: null,
			deleting: false
		});

	const handleDelete = () => {
		setDeleteModal({ ...deleteModal, deleting: true });

		if (deleteModal.multi) {
			const payload = { fileAttachmentIDs: [], pathIDs: [] };
			selectedFiles.forEach(data => {
				if (isPath(data)) payload.pathIDs.push(data.pathID);
				else
					payload.fileAttachmentIDs.push(
						data.fileAttachment.fileAttachmentID
					);
			});

			handleDeleteMultiple(payload)
				.then(() => {
					fetchFiles(false, currentParentPath);
					clearSelected();
				})
				.catch(toastError)
				.finally(() => resetDeleteModal());
		} else {
			const deleteApi = isPath(deleteModal.data)
				? deletePathApi(deleteModal.data.pathID)
				: deleteFileApi(
						deleteModal.data.fileAttachment.fileAttachmentID
					);

			deleteApi
				.then(() => fetchFiles(false, currentParentPath))
				.catch(toastError)
				.finally(() => resetDeleteModal());
		}
	};

	/* Create Folder */
	const createFolder = (values, form) => {
		const payload = { ...values };
		if (values.path) {
			payload.parentPathID = currentParentPath;
			payload.objectID = objectID;
			payload.objectTypeID = objectTypeID;

			return createPathApi(form, payload, { objectID, objectTypeID })
				.then(() => {
					fetchFiles(false, currentParentPath);
					fetchPaths();
				})
				.catch(toastError);
		}
	};

	/* Drag & Drop */
	const onSetDropFolder = pathID => setDropFolder(pathID);

	const onDropFiles = useCallback(
		acceptedFiles => {
			if (disableUpload) return;

			setIsUploading(true);
			const setPath =
				dropFolder || moveFile.newDirectory || currentParentPath;
			const MAX_PARALLEL_UPLOADS = 100;

			const uploadFiles = async files => {
				const promises = files.map(file =>
					uploadFileApi(
						file,
						{
							objectTypeID,
							objectID,
							pathID: setPath,
							includes: 'file'
						},
						{}
					)
				);

				return Promise.allSettled(promises);
			};

			const uploadInBatches = async (files, batchSize) => {
				const results = [];
				for (let i = 0; i < files.length; i += batchSize) {
					const batch = files.slice(i, i + batchSize);
					const batchResults = await uploadFiles(batch);
					results.push(...batchResults);
				}

				return results;
			};

			uploadInBatches(acceptedFiles, MAX_PARALLEL_UPLOADS)
				.then(response => {
					fetchFiles(false, currentParentPath, currentParentPath);
					onSetDropFolder(false);
					if (api.tableActions?.refreshActivities)
						dispatch(api.tableActions.refreshActivities());
					if (api.tableActions?.refreshWatchers)
						dispatch(api.tableActions.refreshWatchers());

					const failedUploads = response.filter(
						r => r.status === 'rejected'
					);
					if (failedUploads.length > 0) {
						failedUploads.forEach(failedUpload => {
							const error =
								failedUpload?.reason?.response?.data?.error;
							toastError(
								error ||
									'An unknown error occurred during file upload.'
							);
						});
					} else {
						toastSuccess('All files uploaded successfully.');
					}
				})
				.catch(() =>
					toastError(
						'An error occurred during the file upload process.'
					)
				)
				.finally(() => setIsUploading(false));
		},
		[
			currentParentPath,
			moveFile,
			dropFolder,
			objectTypeID,
			objectID,
			setIsUploading
		]
	);

	const resetFilesDrag = useCallback(() => {
		setIsDragging(false);
		setFolderContents(prevState =>
			prevState.map(item => ({ ...item, moving: false }))
		);
		resetMoveFile();
	}, []);

	const onDragStart = useCallback(
		(data, index) => {
			if (isMoving) return;
			setIsDragging(true);

			setFolderContents(prevState =>
				prevState.map((item, idx) =>
					idx === index
						? {
								...item,
								moving: true,
								name: isPath(item)
									? `folder ${item.path}`
									: `file ${item.file.title}`
							}
						: item
				)
			);
			setMoveFile(prevState => ({ ...prevState, data }));
		},
		[isMoving]
	);

	const onDragLeave = () =>
		setMoveFile(prevState => ({
			...prevState,
			newDirectory: null,
			newDirectoryName: ''
		}));

	const onRowDragOver = useCallback(
		data => {
			if (isMoving) return;
			if (isPath(data)) {
				setMoveFile(prevState => ({
					...prevState,
					newDirectory: data.pathID,
					newDirectoryName: `${data.path || 'the selected'} folder`
				}));
			}
		},
		[isMoving]
	);

	const onDragEnd = useCallback(() => {
		if (!moveFile.newDirectory) resetFilesDrag();
	}, [moveFile, resetFilesDrag]);

	const onRowDrop = useCallback(
		(targetItem, droppedItem) => {
			if (isMoving || !moveFile.data) return;
			if (!isPath(targetItem)) return resetFilesDrag();
			if (isPath(droppedItem) && droppedItem.pathID === targetItem.pathID)
				return resetFilesDrag();

			if (shouldOpenConfirmMoveModal())
				setConfirmShareWithModal({
					status: true,
					data: targetItem,
					form: null
				});
			else onDrop(targetItem);
		},
		[
			isMoving,
			moveFile.data,
			resetFilesDrag,
			shouldOpenConfirmMoveModal,
			onDrop
		]
	);

	const onDrop = useCallback(
		data => {
			setIsMoving(true);

			const updateAction = isPath(moveFile.data)
				? handlePathUpdate(moveFile.data.pathID, {
						parentPathID: data.pathID
					})
				: handleFileUpdate(
						moveFile.data.fileAttachment.fileAttachmentID,
						{ pathID: data.pathID }
					);

			updateAction
				.then(() => {
					fetchFiles(false, currentParentPath).finally(() =>
						resetFilesDrag()
					);
					toastSuccess(
						`${isPath(moveFile.data) ? 'Folder' : 'File'} moved successfully`
					);
				})
				.catch(error => {
					toastError(error);
					resetFilesDrag();
				})
				.finally(() => {
					setIsMoving(false);
				});
		},
		[
			moveFile.data,
			currentParentPath,
			resetFilesDrag,
			fetchFiles,
			handlePathUpdate,
			handleFileUpdate
		]
	);

	const handleNewPathInput = event =>
		setAddingNewPath({ ...addingNewPath, path: event.target.value });

	const resetAddingNewPath = () =>
		setAddingNewPath({ status: false, path: '', isAdding: false });

	const createNewFolder = () => {
		if (addingNewPath.path) {
			setAddingNewPath({ ...addingNewPath, isAdding: true });

			const payload = {
				parentPathID: currentParentPath,
				objectID: objectID,
				objectTypeID: objectTypeID,
				path: addingNewPath.path
			};

			return createNewPath(payload, { objectID, objectTypeID })
				.then(() => {
					fetchFiles(
						false,
						currentParentPath,
						currentParentPath,
						null,
						false
					);
					fetchPaths();
					resetAddingNewPath();
				})
				.catch(error => {
					toastError(error);
					setAddingNewPath({ ...addingNewPath, isAdding: false });
				});
		}
	};

	const value = {
		objectID,
		objectTypeID,
		contactTypeID,
		portalTypeID,
		disableUpload,
		common: { actions: api.tableActions },
		files,
		folders,
		selectedFiles,
		folderContents,
		breadcrumbs,
		isLoading,
		isDownloading,
		isMoving,
		emptyMessage,
		shareContacts,
		sharedWithMenu,
		deleteModal,
		editModal,
		shareFileModal,
		setShareFileModal,
		isUploading,
		setMoveFile,
		moveFile,
		setAddingNewPath,
		addingNewPath,
		handlePathUpdate,
		handleFileUpdate,
		fetchFiles,
		fetchPaths,
		handleSearch,
		onShareFile,
		handleDownload,
		toggleEditModal,
		toggleMoveFileModal,
		toggleDeleteModal,
		sharedWith,
		handleBatchDownload,
		toggleSharedWithMultiMenu,
		sharedMultiMenuActions,
		handleDelete,
		handleUpdate,
		toggleShareWithMultiModal,
		resetShareModal,
		handleShareMultiSubmit,
		toggleShareWith,
		handleMove,
		confirmMove,
		currentParentPath,
		createNewPath,
		handleNewPathInput,
		createNewFolder,
		resetAddingNewPath,
		onDrop,
		createFolder,
		setClearSelected,
		onRowSelected,
		onRowClick,
		onRowDragOver,
		onDragStart,
		onDragLeave,
		onDragEnd,
		onDropFiles,
		confirmShareWithModal,
		toggleConfirmShareWithModal,
		handleConfirmMove,
		onRowDrop
	};

	return (
		<FilesContext.Provider value={value}>{children}</FilesContext.Provider>
	);
};
